/**
 * Density System Configuration
 * Different UI scale options for various screen sizes and user preferences
 */

export const densityPresets = {
  // Compact - For high-resolution displays with lots of content
  compact: {
    name: 'Compact',
    description: 'Minimal spacing, smallest text (High-DPI displays)',
    scale: 0.85,
    typography: {
      fontSize: {
        xs: '0.6875rem',  // 11px
        sm: '0.75rem',    // 12px
        base: '0.8125rem', // 13px - Minimum readable
        md: '0.9375rem',  // 15px
        lg: '1.0625rem',  // 17px
        xl: '1.25rem',    // 20px
        '2xl': '1.5rem',  // 24px
      },
      body1: '0.9375rem', // 15px
      body2: '0.8125rem', // 13px
    },
    spacing: {
      factor: 0.75, // Reduces all spacing by 25%
      unit: 6,      // 6px base instead of 8px
    },
    components: {
      button: {
        small: 20,
        medium: 26,
        large: 32,
      },
      input: {
        small: 24,
        medium: 28,
        large: 36,
      },
      icon: {
        small: 12,  // ~85% of 14px for visual balance
        medium: 15,  // ~85% of 17px
        large: 17,   // ~85% of 20px
      },
      chip: {
        small: 22,  // Increased for better visibility
        medium: 24,
        large: 28,
      },
      dataGrid: {
        rowHeight: 24,
        headerHeight: 32,
      },
      panel: {
        headerHeight: 36,
        leftSidebarWidth: 420,  // Narrower for compact density
        leftSidebarMinWidth: 360,
      },
    },
  },

  // Comfortable - Default, balanced for most displays
  comfortable: {
    name: 'Comfortable',
    description: 'Balanced spacing and text (Standard displays)',
    scale: 1.0,
    typography: {
      fontSize: {
        xs: '0.75rem',    // 12px
        sm: '0.8125rem',  // 13px
        base: '0.875rem', // 14px - Industry standard
        md: '1rem',       // 16px
        lg: '1.125rem',   // 18px
        xl: '1.375rem',   // 22px
        '2xl': '1.625rem', // 26px
      },
      body1: '1rem',     // 16px - MUI default
      body2: '0.875rem', // 14px - MUI default
    },
    spacing: {
      factor: 1.0,
      unit: 8,     // 8px base (default)
    },
    components: {
      button: {
        small: 24,
        medium: 32,
        large: 40,
      },
      input: {
        small: 28,
        medium: 32,
        large: 40,
      },
      icon: {
        small: 14,  // ~87% of 16px for visual balance with 12px text
        medium: 17,  // ~85% of 20px
        large: 20,   // ~83% of 24px
      },
      chip: {
        small: 24,  // Increased for better visibility
        medium: 26,
        large: 32,
      },
      dataGrid: {
        rowHeight: 28,
        headerHeight: 36,
      },
      panel: {
        headerHeight: 44,
        leftSidebarWidth: 480,  // Default width
        leftSidebarMinWidth: 400,
      },
    },
  },

  // Standard - Slightly larger for regular desktop use
  standard: {
    name: 'Standard',
    description: 'Standard spacing and text (Desktop displays)',
    scale: 1.15,
    typography: {
      fontSize: {
        xs: '0.875rem',   // 14px
        sm: '0.9375rem',  // 15px
        base: '1rem',     // 16px
        md: '1.125rem',   // 18px
        lg: '1.25rem',    // 20px
        xl: '1.5rem',     // 24px
        '2xl': '1.875rem', // 30px
      },
      body1: '1.125rem', // 18px
      body2: '1rem',     // 16px
    },
    spacing: {
      factor: 1.15,
      unit: 9,      // 9px base
    },
    components: {
      button: {
        small: 28,
        medium: 36,
        large: 44,
      },
      input: {
        small: 32,
        medium: 36,
        large: 44,
      },
      icon: {
        small: 15,  // ~83% of 18px for visual balance with 14px text
        medium: 19,  // ~83% of 23px
        large: 24,   // ~86% of 28px
      },
      chip: {
        small: 26,  // Increased for better visibility
        medium: 30,
        large: 36,
      },
      dataGrid: {
        rowHeight: 32,
        headerHeight: 40,
      },
      panel: {
        headerHeight: 48,
        leftSidebarWidth: 520,  // Slightly wider for standard
        leftSidebarMinWidth: 440,
      },
    },
  },

  // Spacious - For larger displays or accessibility
  spacious: {
    name: 'Spacious',
    description: 'Generous spacing, larger text (Large displays, accessibility)',
    scale: 1.3,
    typography: {
      fontSize: {
        xs: '1rem',       // 16px
        sm: '1.0625rem',  // 17px
        base: '1.125rem', // 18px
        md: '1.25rem',    // 20px
        lg: '1.375rem',   // 22px
        xl: '1.625rem',   // 26px
        '2xl': '2.125rem', // 34px
      },
      body1: '1.25rem',   // 20px
      body2: '1.125rem',  // 18px
    },
    spacing: {
      factor: 1.3,
      unit: 10,     // 10px base
    },
    components: {
      button: {
        small: 32,
        medium: 40,
        large: 48,
      },
      input: {
        small: 36,
        medium: 40,
        large: 48,
      },
      icon: {
        small: 18,  // ~86% of 21px for visual balance with 16px text
        medium: 22,  // ~85% of 26px
        large: 26,   // ~84% of 31px
      },
      chip: {
        small: 28,  // Increased for better visibility
        medium: 32,
        large: 40,
      },
      dataGrid: {
        rowHeight: 36,
        headerHeight: 44,
      },
      panel: {
        headerHeight: 52,
        leftSidebarWidth: 560,  // Wider for spacious density
        leftSidebarMinWidth: 480,
      },
    },
  },

  // Large - For TV displays or vision accessibility
  large: {
    name: 'Large',
    description: 'Extra large for TV displays or vision accessibility',
    scale: 1.5,
    typography: {
      fontSize: {
        xs: '1.125rem',   // 18px
        sm: '1.1875rem',  // 19px
        base: '1.25rem',  // 20px
        md: '1.375rem',   // 22px
        lg: '1.5rem',     // 24px
        xl: '1.875rem',   // 30px
        '2xl': '2.5rem',  // 40px
      },
      body1: '1.375rem',  // 22px
      body2: '1.25rem',   // 20px
    },
    spacing: {
      factor: 1.5,
      unit: 12,     // 12px base
    },
    components: {
      button: {
        small: 36,
        medium: 48,
        large: 56,
      },
      input: {
        small: 40,
        medium: 48,
        large: 56,
      },
      icon: {
        small: 20,  // ~83% of 24px for visual balance with 20px text
        medium: 26,  // ~87% of 30px
        large: 30,   // ~83% of 36px
      },
      chip: {
        small: 30,  // Increased for better visibility
        medium: 36,
        large: 44,
      },
      dataGrid: {
        rowHeight: 40,
        headerHeight: 48,
      },
      panel: {
        headerHeight: 60,
        leftSidebarWidth: 640,  // Much wider for large density
        leftSidebarMinWidth: 540,
      },
    },
  },
};

/**
 * Get density configuration by name
 */
export const getDensity = (densityName = 'comfortable') => {
  return densityPresets[densityName] || densityPresets.comfortable;
};

/**
 * Apply density to theme
 */
export const applyDensity = (theme, density) => {
  const preset = getDensity(density);
  
  // Deep clone theme to avoid mutations
  const newTheme = JSON.parse(JSON.stringify(theme));
  
  // Apply typography sizes
  if (preset.typography?.fontSize) {
    newTheme.typography.fontSize = preset.typography.fontSize;
  }
  
  // Apply spacing factor
  if (preset.spacing?.unit) {
    const originalSpacing = theme.spacing;
    newTheme.spacing = (factor) => {
      if (typeof factor === 'number') {
        return `${factor * preset.spacing.unit}px`;
      }
      return originalSpacing(factor);
    };
  }
  
  // Apply component dimensions
  if (preset.components) {
    newTheme.dimensions = {
      ...theme.dimensions,
      ...preset.components,
    };
  }
  
  // Store current density
  newTheme.density = density;
  
  return newTheme;
};

/**
 * Hook to get responsive density based on screen size
 */
export const getAutoDensity = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const dpi = window.devicePixelRatio || 1;
  
  // Auto-detect based on screen characteristics
  if (width <= 1366) {
    return 'compact';
  } else if (width <= 1920) {
    if (dpi > 1.5) {
      return 'compact';
    }
    return 'comfortable';
  } else if (width <= 2560) {
    return 'standard';
  } else if (width <= 3840) {
    return 'spacious';
  } else {
    return 'large';
  }
};

/**
 * Screen size recommendations
 */
export const densityRecommendations = {
  compact: {
    screens: ['1366x768', '1440x900', 'High-DPI laptops'],
    use: 'When you need to see lots of data at once',
  },
  comfortable: {
    screens: ['1920x1080', '1920x1200', 'Standard monitors'],
    use: 'Default for most desktop displays',
  },
  standard: {
    screens: ['2560x1440', '2560x1600', 'QHD displays'],
    use: 'Better readability on larger monitors',
  },
  spacious: {
    screens: ['3440x1440', '3840x2160', 'Ultrawide/4K'],
    use: 'Comfortable viewing on high-res displays',
  },
  large: {
    screens: ['TV displays', 'Projectors', 'Accessibility'],
    use: 'Maximum readability from distance',
  },
};